import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Users, Calendar as CalIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import { toast } from 'sonner';

/**
 * Subcontractor-wise labour attendance for a single project.
 * Daily entry per subcontractor with named workers (P/A/H).
 */
export default function LabourPanel({ projectId }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [subs, setSubs] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ days: [], by_trade: {}, total_mandays: 0 });
  const [adding, setAdding] = useState(null); // sub object
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const [s, attRows, sm] = await Promise.all([
        apiGet('/subcontractors'),
        apiGet(`/labour-attendance/by-project/${projectId}`, { date }),
        apiGet(`/labour-attendance/summary/${projectId}`),
      ]);
      setSubs(s); setRows(attRows); setSummary(sm);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { if (projectId) load(); /* eslint-disable-next-line */ }, [projectId, date]);

  const todayBySub = useMemo(() => {
    const m = {};
    rows.forEach(r => { m[r.subcontractor_id] = r; });
    return m;
  }, [rows]);

  const dayTotal = rows.reduce((s, r) => s + (r.manday_equivalent || 0), 0);
  const totalPresent = rows.reduce((s, r) => s + (r.total_present || 0), 0);
  const totalAbsent = rows.reduce((s, r) => s + (r.total_absent || 0), 0);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="kpi-overline">Labour Attendance</div>
          <h3 className="font-display font-bold text-base mt-1">Subcontractor-wise daily register</h3>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">For Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="labour-date-input"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-2.5 text-center">
            <div className="kpi-overline">Day Mandays</div>
            <div className="font-display font-extrabold text-xl tabular-nums">{dayTotal.toFixed(1)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-2.5 text-center">
            <div className="kpi-overline text-emerald-700">Total Present</div>
            <div className="font-display font-extrabold text-xl tabular-nums text-emerald-700" data-testid="labour-total-present">{totalPresent}</div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-sm px-4 py-2.5 text-center">
            <div className="kpi-overline text-rose-700">Total Absent</div>
            <div className="font-display font-extrabold text-xl tabular-nums text-rose-700" data-testid="labour-total-absent">{totalAbsent}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-2">
          {subs.length === 0 && <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-xs uppercase tracking-widest font-display text-slate-400">No subcontractors registered</div>}
          {subs.map(sub => {
            const today = todayBySub[sub.id];
            const isOpen = expanded === sub.id;
            return (
              <div key={sub.id} data-testid={`labour-sub-${sub.id}`} className="bg-white border border-slate-200 rounded-sm">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-indigo-50 flex items-center justify-center">
                      <Users size={18} className="text-[#3b4872]" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-sm">{sub.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{sub.specialty}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {today ? (
                      <div className="flex items-center gap-3 mr-2 text-xs">
                        <span className="text-emerald-700 font-display font-bold tabular-nums">P {today.total_present}</span>
                        <span className="text-amber-700 font-display font-bold tabular-nums">H {today.half_day_count}</span>
                        <span className="text-red-700 font-display font-bold tabular-nums">A {today.total_absent}</span>
                        <span className="font-display font-extrabold tabular-nums text-slate-900">= {today.manday_equivalent}</span>
                      </div>
                    ) : <span className="text-[11px] uppercase tracking-widest font-display text-slate-400 mr-2">Not marked</span>}
                    <button onClick={() => setAdding(sub)} data-testid={`labour-mark-btn-${sub.id}`}
                      className="px-3 py-1.5 text-[11px] bg-[#3b4872] text-white rounded-sm font-display font-semibold">
                      {today ? 'Edit' : 'Mark'}
                    </button>
                    <button onClick={() => setExpanded(isOpen ? null : sub.id)} className="p-1.5 text-slate-400 hover:text-slate-700">
                      {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>
                {isOpen && today && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                    <div className="kpi-overline mb-2">Worker register · {today.workers?.length || 0}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {(today.workers || []).map((w, i) => (
                        <div key={i} className="flex items-center justify-between bg-white border border-slate-200 px-2.5 py-1.5 rounded-sm text-xs">
                          <span className="truncate">{w.name}</span>
                          <span className={`px-1.5 py-0.5 rounded-sm font-display font-bold text-[10px] ${w.status === 'P' ? 'bg-emerald-50 text-emerald-700' : w.status === 'H' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{w.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="kpi-overline mb-3">Trade-wise mandays (lifetime)</div>
            {Object.entries(summary.by_trade).length === 0 && <div className="text-xs text-slate-400">No data yet</div>}
            {Object.entries(summary.by_trade).map(([t, v]) => (
              <div key={t} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-xs">
                <span className="text-slate-600">{t}</span>
                <span className="font-display font-bold tabular-nums">{v.toFixed(1)}</span>
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="kpi-overline mb-3 flex items-center gap-1"><CalIcon size={11} /> Recent days</div>
            {summary.days.slice(0, 7).map(d => (
              <div key={d.date} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-xs">
                <span className="font-mono text-slate-500">{d.date}</span>
                <span className="font-display font-bold tabular-nums">{d.mandays.toFixed(1)} MD</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {adding && <MarkAttendanceModal projectId={projectId} sub={adding} date={date} existing={todayBySub[adding.id]}
        onClose={() => setAdding(null)} onSaved={() => { setAdding(null); load(); }} />}
    </div>
  );
}

function MarkAttendanceModal({ projectId, sub, date, existing, onClose, onSaved }) {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trade, setTrade] = useState(existing?.trade || sub.specialty || '');

  useEffect(() => {
    (async () => {
      try {
        const wlist = await apiGet(`/workers/by-subcontractor/${sub.id}`);
        const statusMap = {};
        (existing?.workers || []).forEach(w => { statusMap[w.worker_id || w.name] = w.status; });
        setWorkers(wlist.map(w => ({ worker_id: w.id, name: w.name, trade: w.trade, status: statusMap[w.id] || statusMap[w.name] || 'P' })));
      } catch {}
      setLoading(false);
    })();
  }, [sub.id]);

  const cycle = (i) => {
    const order = ['P', 'H', 'A'];
    setWorkers(prev => prev.map((w, idx) => idx === i ? { ...w, status: order[(order.indexOf(w.status) + 1) % 3] } : w));
  };

  const setAll = (s) => setWorkers(prev => prev.map(w => ({ ...w, status: s })));

  const addWorker = () => setWorkers(prev => [...prev, { worker_id: null, name: '', trade: trade, status: 'P', _new: true }]);

  const submit = async () => {
    const cleaned = workers.filter(w => w.name && w.name.trim());
    try {
      await apiPost('/labour-attendance/mark', {
        project_id: projectId, subcontractor_id: sub.id, subcontractor_name: sub.name,
        trade, date, workers: cleaned,
      });
      // Persist new workers to register
      for (const w of cleaned.filter(w => w._new)) {
        await apiPost('/workers', { name: w.name, trade: w.trade || trade, subcontractor_id: sub.id, subcontractor_name: sub.name });
      }
      toast.success('Attendance saved');
      onSaved();
    } catch { toast.error('Failed to save'); }
  };

  const counts = { P: 0, H: 0, A: 0 };
  workers.forEach(w => { counts[w.status] = (counts[w.status] || 0) + 1; });
  const md = counts.P + 0.5 * counts.H;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Mark Attendance</div>
          <h3 className="font-display font-bold text-xl mt-1">{sub.name}</h3>
          <div className="text-xs text-slate-500 mt-1">Date: {date} · Tap a card to cycle P → H → A</div>
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3 gap-3">
            <input value={trade} onChange={e => setTrade(e.target.value)} placeholder="Trade (e.g. Civil)" className="px-3 py-2 border border-slate-300 rounded-sm text-sm flex-1" />
            <div className="flex gap-1">
              <button onClick={() => setAll('P')} className="px-2 py-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm font-display font-bold">All P</button>
              <button onClick={() => setAll('A')} className="px-2 py-1 text-[10px] bg-red-50 text-red-700 border border-red-200 rounded-sm font-display font-bold">All A</button>
            </div>
          </div>
          {loading ? <div className="text-xs text-slate-400 py-8 text-center">Loading…</div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {workers.map((w, i) => (
                <div key={i} onClick={() => cycle(i)}
                  data-testid={`worker-row-${i}`}
                  className="bg-white border border-slate-200 rounded-sm p-2.5 flex items-center justify-between cursor-pointer hover:border-[#3b4872]/50">
                  <div className="flex-1 min-w-0">
                    {w._new ? (
                      <input value={w.name} onClick={e => e.stopPropagation()} onChange={e => setWorkers(prev => prev.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                        placeholder="Worker name" className="w-full text-sm border-b border-slate-200 focus:outline-none" />
                    ) : (
                      <>
                        <div className="text-sm truncate">{w.name}</div>
                        <div className="text-[10px] text-slate-400 uppercase tracking-widest">{w.trade}</div>
                      </>
                    )}
                  </div>
                  <span className={`ml-2 w-7 h-7 rounded-sm flex items-center justify-center font-display font-extrabold text-sm ${w.status === 'P' ? 'bg-emerald-100 text-emerald-700' : w.status === 'H' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{w.status}</span>
                </div>
              ))}
            </div>
          )}
          <button onClick={addWorker} className="mt-3 text-xs text-[#3b4872] font-display font-semibold flex items-center gap-1"><Plus size={12} /> Add walk-in worker</button>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="text-xs text-slate-600">
            P: <b className="tabular-nums">{counts.P}</b> · H: <b className="tabular-nums">{counts.H}</b> · A: <b className="tabular-nums">{counts.A}</b> · Mandays: <b className="tabular-nums">{md.toFixed(1)}</b>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
            <button onClick={submit} data-testid="labour-save-btn" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Save Attendance</button>
          </div>
        </div>
      </div>
    </div>
  );
}
